import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import { AnalyticsProvider, type AnalyticsEvent } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';

import { ForwardBadge } from './analytics-badges';

const PROVIDERS = Object.values(AnalyticsProvider);

const NONE = <span className="text-muted-foreground">—</span>;

/**
 * Column model of the event log: one forwarding column per platform. The log has no sorts (newest
 * first); `meta.filterKey` ties columns to its filters.
 */
export function useEventColumns() {
  const { t } = useTranslation('analytics');
  const format = useFormat();

  return useMemo<Array<DataTableColumnDef<AnalyticsEvent>>>(
    () => [
      {
        id: 'time',
        header: t('columns.time'),
        cell: ({ row }) => (
          <span className="whitespace-nowrap" title={format.utc(row.original.occurredAt)}>
            {format.dateTime(row.original.occurredAt)}
          </span>
        ),
        meta: { filterKey: 'occurredAt', lockVisibility: true },
        size: 170,
      },
      {
        id: 'event',
        header: t('columns.event'),
        cell: ({ row }) => (
          <span className="flex flex-col gap-0.5">
            <span className="font-medium">{t(`eventNames.${row.original.name}`)}</span>
            <code className="text-muted-foreground font-mono text-xs">{row.original.name}</code>
          </span>
        ),
        meta: { filterKey: 'name' },
      },
      {
        id: 'session',
        header: t('columns.session'),
        cell: ({ row }) =>
          row.original.sessionId ? (
            <code
              className="block max-w-36 truncate font-mono text-xs"
              title={row.original.sessionId}
            >
              {row.original.sessionId}
            </code>
          ) : (
            NONE
          ),
        meta: { filterKey: 'sessionId' },
      },
      {
        id: 'order',
        header: t('columns.order'),
        cell: ({ row }) =>
          row.original.orderId ? (
            <code
              className="block max-w-36 truncate font-mono text-xs"
              title={row.original.orderId}
            >
              {row.original.orderId}
            </code>
          ) : (
            NONE
          ),
        meta: { filterKey: 'orderId' },
      },
      {
        id: 'value',
        header: t('columns.value'),
        cell: ({ row }) => (
          <span className="tabular-nums">
            {row.original.value ? format.money(row.original.value) : NONE}
          </span>
        ),
        meta: { align: 'end' },
      },
      ...PROVIDERS.map((provider): DataTableColumnDef<AnalyticsEvent> => ({
        id: `forward-${provider}`,
        header: t(`providers.${provider}`),
        cell: ({ row }) => (
          <ForwardBadge
            provider={t(`providers.${provider}`)}
            forward={row.original.forwarded[provider]}
          />
        ),
      })),
    ],
    [format, t],
  );
}
